import React, { useEffect, useMemo, useState } from 'react';

import { useLoaderData } from 'react-router-dom';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { UnitTypeWithImages } from '@/standard-unit-listing/screens';

import classes from './styles.module.scss';

export interface UnitTypeImageGalleryProps {
    unitType: UnitTypeWithImages;
}

/**
 * Displays the primary unit type image inside the grey card container with a
 * row of carousel dots (primary image + slides) overlaid at the bottom.
 */
export default function UnitTypeImageGallery({
    unitType,
}: UnitTypeImageGalleryProps) {
    const location = useLoaderData() as Location;

    const images = useMemo(() => {
        const list: string[] = [];
        if (unitType.picture) list.push(unitType.picture);
        if (!unitType.picture && location.skin.defaultUnitPicture) list.push(location.skin.defaultUnitPicture);
        if (Array.isArray(unitType.slides)) {
            unitType.slides.forEach((s) => {
                if (s && !list.includes(s)) list.push(s);
            });
        }
        return list;
    }, [unitType.picture, unitType.slides, location.skin.defaultUnitPicture]);

    const [activeIdx, setActiveIdx] = useState(0);

    // Reset to the first image when the unit (variant) changes, so the active
    // dot and image stay in sync when the underlying image set changes.
    useEffect(() => {
        setActiveIdx(0);
    }, [unitType._id]);

    if (!images.length) return null;

    return (
        <div className={classes.imageGallery}>
            <div className={classes.imageWrapper}>
                <img
                    src={images[Math.min(activeIdx, images.length - 1)]}
                    alt={unitType.typeName}
                    className={classes.picture}
                />
                {images.length > 1 && (
                    <div
                        className={classes.dots}
                        role="list"
                        aria-label="Image selector"
                    >
                        {images.map((src, idx) => (
                            <button
                                key={src + idx}
                                type="button"
                                aria-label={`Select image ${idx + 1}`}
                                aria-current={activeIdx === idx}
                                onClick={() => setActiveIdx(idx)}
                                className={`${classes.dot} ${
                                    activeIdx === idx ? classes.dotActive : ''
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
